import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector } from '@/store';
import { useSendVerificationEmailMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAccountColors } from '@/constants/residents/mobile/accountColors';
import { useAccountSectionStyles } from '@/constants/residents/mobile/accountSectionStyles';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { strings, formatString } from '@/constants/residents/strings';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const MailIcon = SVG_COMPONENTS.SEND_INVITATION;

/**
 * Mobile Reset Password Section
 * Displays reset password instructions and button
 * Based on Figma design "3. Password"
 */
export const MobileResetPasswordSection: React.FC = () => {
  const ac = useAccountColors();
  const sectionStyles = useAccountSectionStyles();
  const styles = useMemo(() => makeStyles(ac), [ac]);
  const insets = useSafeAreaInsets();
  const { showError, showSuccess } = useToast();
  const user = useAppSelector((s) => (s as any).auth?.user);
  const email = user?.email || '';

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [sendVerificationEmail, { isLoading }] = useSendVerificationEmailMutation();

  const handleResetPassword = useCallback(async () => {
    if (!email) {
      showError('No email associated with this account.');
      return;
    }

    try {
      await sendVerificationEmail({ 
        email, 
        targetPath: '/account/account-reset-password/success' 
      }).unwrap();
      
      // Show verification modal instead of navigating away
      setShowVerificationModal(true);
    } catch (_e) {
      showError('Failed to send verification email');
    }
  }, [email, sendVerificationEmail, showError]);

  const handleResendEmail = useCallback(async () => {
    if (!email) return;
    
    try {
      await sendVerificationEmail({ 
        email, 
        targetPath: '/account/account-reset-password/success' 
      }).unwrap();
      showSuccess('Verification email resent');
    } catch (_e) {
      showError('Failed to resend verification email');
    }
  }, [email, sendVerificationEmail, showSuccess, showError]);

  const handleCloseModal = useCallback(() => {
    setShowVerificationModal(false);
  }, []);

  return (
    <View style={sectionStyles.container}>
      {/* Header */}
      <View style={sectionStyles.header}>
        <Text style={sectionStyles.headerTitle}>{strings.account.resetPasswordTitle}</Text>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <Text style={styles.description}>
          {strings.account.resetPasswordCopy}
        </Text>

        {/* Reset Password Button */}
        <TouchableOpacity
          style={sectionStyles.actionButton}
          onPress={handleResetPassword}
          disabled={isLoading}
          activeOpacity={0.8}
        >
          <Text style={sectionStyles.actionButtonText}>
            {isLoading ? 'Sending...' : strings.account.resetPasswordButton}
          </Text>
          
          <View style={styles.buttonRight}>
            {isLoading ? (
              <ActivityIndicator size="small" color={ac.textPrimary} />
            ) : (
              <ChevronRightIcon width={24} height={24} color={ac.textPrimary} />
            )}
          </View>
        </TouchableOpacity>
      </View>

      {/* Verification Email Sent Modal */}
      <Modal
        visible={showVerificationModal}
        animationType="fade"
        transparent={false}
        onRequestClose={handleCloseModal}
      >
        <View style={[sectionStyles.modalContainer, { paddingTop: insets.top }]}>
          <View style={sectionStyles.modalContent}>
            <View style={sectionStyles.modalBodyWide}>
              {/* Mail Icon */}
              <View style={sectionStyles.modalIconContainerLarge}>
                {MailIcon && (
                  <MailIcon width={64} height={64} color={ac.textSecondary} />
                )}
              </View>

              {/* Title */}
              <Text style={sectionStyles.modalTitle}>
                {strings.account.resetPasswordModalTitle}
              </Text>

              {/* Message */}
              <Text style={sectionStyles.modalMessageSmall}>
                {formatString(strings.account.resetPasswordModalMessagePrefix, email)}
              </Text>
            </View>

            {/* Buttons */}
            <View style={[sectionStyles.modalFooter, { paddingBottom: Math.max(insets.bottom, 24) }]}>
              <TouchableOpacity
                style={sectionStyles.cancelButton}
                onPress={handleResendEmail}
                disabled={isLoading}
              >
                <Text style={sectionStyles.cancelButtonText}>
                  {isLoading ? 'Sending...' : strings.account.resetPasswordModalResend}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={sectionStyles.confirmButton}
                onPress={handleCloseModal}
              >
                <Text style={sectionStyles.confirmButtonText}>
                  {strings.account.resetPasswordModalClose}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

        </View>
      </Modal>
    </View>
  );
};

const makeStyles = (ac: ReturnType<typeof useAccountColors>) => StyleSheet.create({
  content: {
    padding: 20,
    gap: 16,
  },
  description: {
    fontSize: 16,
    fontWeight: '400',
    color: ac.textPrimary,
    lineHeight: 24,
  },
  buttonRight: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: 28,
  },
});

export default MobileResetPasswordSection;
